import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as _ from 'lodash-es';

import { useLoop } from '../store/Loop';
import { useMappingProperty } from '../store/Mapping';
import { useBusinessReportEditor } from '../store/Report';
import { useAxiosClient } from '../store/AxiosClient';
import { useCustomClaims } from '../store/CustomClaims';
import { useEntityMeta } from '../store/EntityMeta';

import { useStateWithGetter } from '../../utils/useStateWithGetter';
import { useThrowError } from '../../utils/useThrowError';
import { translate } from '../../utils/translations';

import {
  BffCustomReportsApi,
  MappingDefinitionJsonModel,
  useApi,
} from './generated';
import { DataCatalog } from '../../types';
import { PAGE_PATH } from '../../constants';

/**
 * 初期の帳票
 * データカタログ
 * マッピング情報
 * を取得する
 */
export const useInit = () => {
  const { setDataCatalog, setReportFormatFile, setIsInitialRequestComplete } =
    useBusinessReportEditor();
  const {
    setMappingRowVersion,
    setTemplateId,
    setTemplateRowVersion,
    setMappingCreatedAt,
  } = useEntityMeta();
  const { restoreMappingProperties } = useMappingProperty();
  const { restoreLoops } = useLoop();
  const {
    customClaims: { reportNameAlias, templateVersion },
  } = useCustomClaims();
  const { throwError } = useThrowError();
  const navigate = useNavigate();
  const location = useLocation();

  const axiosClient = useAxiosClient();
  const customReportApi = useApi(axiosClient, BffCustomReportsApi);

  const [isApiRequesting, setIsApiRequesting, getIsApiRequesting] =
    useStateWithGetter(false);
  const [isInitialized, setIsInitialized, getIsInitialized] =
    useStateWithGetter(false);

  useEffect(() => {
    if (getIsApiRequesting() || getIsInitialized()) return;

    const fetchFiles = async () => {
      setIsApiRequesting(true);

      try {
        const {
          data: { data, errorCode, success },
        } = await customReportApi.customReportsGetInit(
          reportNameAlias,
          templateVersion
        );

        console.log('errorCode:', errorCode, 'success:', success);
        if (!success) {
          throwError(translate('ERROR.FAILED_TO_GET_INITIAL_DATA'));
          return;
        }

        const readOnly = _.get(data, 'readOnly');
        const canSave = _.get(data, 'canSave');
        if (readOnly && !canSave && location.pathname === PAGE_PATH.EDIT) {
          // 本番適用済みの帳票は編集できないようにする
          navigate(PAGE_PATH.DETAIL);
          return;
        }

        // データカタログをストアに登録
        const dataCatalog: DataCatalog = data?.templates
          .dataCatalog as DataCatalog;
        dataCatalog.categories = _.orderBy(
          dataCatalog.categories,
          ['categoryOrderNumber'],
          ['asc']
        );
        dataCatalog.categories.forEach((category) => {
          category.properties = _.orderBy(
            category.properties,
            ['propertyOrderNumber'],
            ['asc']
          );
        });
        setDataCatalog(dataCatalog);

        // マッピング情報をストアに登録
        const excelMapping = data?.templates
          .mappingDefinition as MappingDefinitionJsonModel;
        const mappingProperties = _.flatMap(excelMapping.sheets, (sheet) =>
          _.map(sheet.mappingProperties, (prop) => ({
            ...prop,
            sheetId: sheet.sheetId,
            categoryId: 0,
            mappingRestriction: '',
          }))
        );

        const loopMappingProperties = _.flatMap(excelMapping.sheets, (sheet) =>
          _.flatMap(sheet.loopMappingProperties, (loopProp) =>
            _.map(loopProp.mappingProperties, (prop) => ({
              ...prop,
              sheetId: sheet.sheetId,
              categoryId: loopProp.categoryId,
              mappingRestriction: loopProp.mappingRestriction,
            }))
          )
        );

        const loops = _.flatMap(excelMapping.sheets, (sheet) =>
          _.map(sheet.loopMappingProperties, (loop) => {
            return {
              loopId: loop.loopId,
              loopCount: loop.loopCount,
              loopRange: loop.loopRange,
              loopHeaderRange: loop.mappingEnableCellRange,
              categoryId: loop.categoryId,
              mappingRestriction: loop.mappingRestriction,
              sheetId: sheet.sheetId,
            };
          })
        );

        // loopMappingの方のmappingPropertyも分解してmappingPropertiesに登録する
        restoreMappingProperties([
          ...mappingProperties,
          ...loopMappingProperties,
        ]);
        restoreLoops(loops);

        const res = await fetch(
          `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data?.templates.template ?? ''}`
        );
        const blob = await res.blob();
        setReportFormatFile({ file: blob, source: 'api' });

        setTemplateId(data?.templates.templateId ?? 0);
        setTemplateRowVersion(data?.templates.templateRowVersion ?? '');
        setMappingRowVersion(data?.templates.mappingDefinitionRowVersion ?? '');
        setMappingCreatedAt(excelMapping.createdAt ?? '');

        setIsInitialized(true);
        setIsInitialRequestComplete(true);
      } catch (e) {
        // リクエスト自体が失敗したパターン（200以外）
        throwError(e);
      } finally {
        setIsApiRequesting(false);
      }
    };

    fetchFiles();
  }, [reportNameAlias, templateVersion]);
};
